import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Facebook, Instagram, Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CONTACT, waLink, SKYLINE_BG } from "@/lib/config";
import { BRANDS } from "@/data/brands";

const CHANNELS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    detail: CONTACT.whatsappDisplay,
    href: waLink("Hello Infra Edge, I want to discuss a bulk cement requirement."),
    testid: "contact-channel-whatsapp",
    external: true,
  },
  {
    icon: Facebook,
    label: "Facebook",
    detail: "@infraedge",
    href: CONTACT.facebook,
    testid: "contact-channel-facebook",
    external: true,
  },
  {
    icon: Instagram,
    label: "Instagram",
    detail: "@infraedge",
    href: CONTACT.instagram,
    testid: "contact-channel-instagram",
    external: true,
  },
  {
    icon: Mail,
    label: "Email",
    detail: CONTACT.email,
    href: `mailto:${CONTACT.email}?subject=Bulk Cement Inquiry`,
    testid: "contact-channel-email",
    external: false,
  },
];

const inputCls =
  "w-full bg-ink border border-white/10 focus:border-brand/60 outline-none text-sm text-gray-200 placeholder-gray-600 px-4 py-3 transition-colors";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", location: "", brand: "", quantity: "", dispatch_date: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/rfq", form);
      toast.success(`Inquiry logged — reference ${data.ref}. Our dispatch desk will reach out shortly.`);
      setForm({ name: "", company: "", phone: "", location: "", brand: "", quantity: "", dispatch_date: "", notes: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-surface border-t border-white/10 py-20 lg:py-28 grain overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img src={SKYLINE_BG} alt="" className="w-full h-full object-cover opacity-[0.07]" />
        <div className="absolute inset-0 bg-gradient-to-b from-surface/90 via-surface/80 to-surface" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-brand inline-block" /> Bulk Dispatch Desk
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
              Get a Plant-Direct
              <br />
              Quote in Hours
            </h2>
            <p className="mt-5 text-sm sm:text-base text-gray-400 leading-relaxed max-w-md">
              Share your site requirement and our dispatch desk responds with brand-wise landed
              rates, freight options and the nearest live corridor.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CHANNELS.map((c, i) => (
                <motion.a
                  key={c.label}
                  href={c.href}
                  target={c.external ? "_blank" : undefined}
                  rel={c.external ? "noopener noreferrer" : undefined}
                  data-testid={c.testid}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group flex items-center gap-4 bg-ink border border-white/10 hover:border-brand/50 p-4 transition-colors"
                >
                  <span className="p-2.5 bg-surface border border-white/10 text-brand group-hover:bg-brand group-hover:text-ink transition-colors">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-heading font-bold uppercase text-sm text-white">{c.label}</span>
                    <span className="block font-mono text-xs text-gray-500 mt-0.5">{c.detail}</span>
                  </span>
                </motion.a>
              ))}
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            data-testid="rfq-form-container"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-elevated border border-white/10 p-6 sm:p-8 relative"
          >
            <div className="absolute -top-3 left-6 bg-brand text-ink font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1">
              RFQ / Bulk Inquiry
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <input required value={form.name} onChange={set("name")} placeholder="Your name *" data-testid="rfq-input-contractor-name" className={inputCls} />
              <input value={form.company} onChange={set("company")} placeholder="Firm / company" data-testid="rfq-input-company" className={inputCls} />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone (WhatsApp)" data-testid="rfq-input-phone" className={inputCls} />
              <input value={form.location} onChange={set("location")} placeholder="Delivery city / pincode" data-testid="rfq-input-location" className={inputCls} />
              <select value={form.brand} onChange={set("brand")} data-testid="rfq-select-brand" className={inputCls}>
                <option value="">Brand preference</option>
                {BRANDS.map((b) => (
                  <option key={b.id} value={b.name}>{b.name}</option>
                ))}
                <option value="Any / Best Rate">Any / Best rate</option>
              </select>
              <input value={form.quantity} onChange={set("quantity")} placeholder="Est. quantity (bags / tonnes)" data-testid="rfq-input-quantity" className={inputCls} />
              <input value={form.dispatch_date} onChange={set("dispatch_date")} placeholder="Target dispatch date" className={`${inputCls} sm:col-span-2`} />
              <textarea value={form.notes} onChange={set("notes")} placeholder="Site notes, access constraints, unloading window…" rows={3} className={`${inputCls} sm:col-span-2 resize-none`} />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="rfq-btn-submit-inquiry"
              className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-dark disabled:opacity-60 text-ink font-heading font-bold uppercase tracking-wide px-6 py-4 transition-colors"
            >
              {submitting ? "Logging inquiry…" : (
                <>
                  <Send className="h-4 w-4" /> Submit Bulk Inquiry
                </>
              )}
            </button>
            <p className="mt-4 flex items-center gap-2 font-mono text-[11px] text-gray-500">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              Response within 4 working hours · No spam, ever
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
